import { memo, useEffect, useRef } from 'react';

import { type Editor as TiptapEditor } from '@tiptap/core';

import {
  toAnchor,
  useCommentState,
  useComments,
} from '@spotify/plugin-confidence-comments-react';

import { useBlockCommentCleanup } from '../hooks';

type EditorCommentsProps = {
  editor: TiptapEditor;
  resource: string;
  children: JSX.Element;
};

export const EditorComments = memo(
  ({ editor, resource, children }: EditorCommentsProps) => {
    const selectComment = useCommentState(state => state.selectComment);
    const enterCommentMode = useCommentState(state => state.enterCommentMode);
    const { comments } = useComments({ resource });

    const commentsRef = useRef(comments);
    const processedResolvedCommentsRef = useRef<Set<string>>(new Set());

    useBlockCommentCleanup(editor, resource);

    useEffect(() => {
      commentsRef.current = comments;
    }, [comments]);

    useEffect(() => {
      if (!editor || editor.isDestroyed) return undefined;

      const handleClick = (event: MouseEvent) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const commentSpan = target.closest('[data-comment-id]');

        if (!commentSpan) return;

        const zoneId = commentSpan.getAttribute('data-comment-id');
        if (!zoneId) return;

        const currentComments = commentsRef.current;
        if (!currentComments) return;

        const comment = currentComments.find(c => {
          const anchor = toAnchor(c);
          return anchor && anchor.zone === zoneId;
        });

        if (comment) {
          const anchor = toAnchor(comment);
          if (anchor) {
            selectComment(comment.name, anchor);
            enterCommentMode();
          }
        }
      };

      const editorElement = editor.view.dom;
      editorElement.addEventListener('click', handleClick);

      return () => editorElement.removeEventListener('click', handleClick);
    }, [editor, selectComment, enterCommentMode]);

    useEffect(() => {
      if (!editor || editor.isDestroyed || !comments) return;

      comments.forEach(comment => {
        const anchor = toAnchor(comment);
        if (!anchor) return;

        const zoneId = anchor.zone;
        if (!zoneId.startsWith('tiptap-comment-')) return;

        if (comment.resolved) {
          if (processedResolvedCommentsRef.current.has(zoneId)) return;

          editor.commands.removeCommentById(zoneId);
          editor.view.dispatch(
            editor.state.tr.setMeta('removePendingComment', zoneId),
          );
          processedResolvedCommentsRef.current.add(zoneId);
        } else {
          processedResolvedCommentsRef.current.delete(zoneId);
        }
      });
    }, [editor, comments]);

    return children;
  },
);

EditorComments.displayName = 'EditorComments';
